import { Link } from "react-router-dom";
import { CheckCircle2, GraduationCap, Target, HeartHandshake, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, BookButton, SectionHeading, FinalCTA } from "@/components/shared";
import { business, images, areasOfExpertise, routes } from "@/lib/site";
import { combine, physicianSchema, breadcrumbSchema } from "@/lib/schema";

const philosophy = [
  { title: "Understand the Concern", desc: "We start by listening and assessing to understand the underlying cause, not just the symptom." },
  { title: "Personalized Care", desc: "Every care plan is tailored to your body, lifestyle and goals." },
  { title: "Non-Surgical Approach", desc: "Conservative, hands-on care options where appropriate for your condition." },
  { title: "Patient Education", desc: "We help you understand your body so you can care for it with confidence." },
];

export default function About() {
  const jsonLd = combine(
    physicianSchema,
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "About Dr. Satyam", path: routes.about },
    ])
  );

  return (
    <>
      <SEO
        path={routes.about}
        title="About Dr. Satyam Jaiswal | Chiropractor in Varanasi"
        description="Meet Dr. Satyam Jaiswal — a chiropractor and pain & spine care professional in Varanasi. Learn about his qualifications, expertise and patient-focused treatment philosophy."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="About the Doctor"
        title="Meet Dr. Satyam Jaiswal"
        sub="Professional chiropractic care for better movement and pain relief in Varanasi."
        breadcrumb={[{ label: "About Dr." }]}
      />

      {/* Intro */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-navy">
              <div className="absolute inset-0 dotted-grid opacity-20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-[70%] w-[70%] rounded-full bg-brand-orange/20 blur-3xl" />
              </div>
              <img
                src={images.doctorHero}
                alt="Dr. Satyam Jaiswal chiropractor Varanasi portrait"
                className="relative z-10 mx-auto w-full max-w-md object-contain drop-shadow-2xl"
                loading="eager"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">Professional Introduction</span>
            <h2 className="font-heading font-extrabold tracking-tight text-navy text-3xl sm:text-4xl leading-[1.1]">
              A Patient-Focused Chiropractor in Varanasi
            </h2>
            <p className="mt-5 text-ink leading-relaxed">
              Dr. Satyam Jaiswal is dedicated to helping individuals improve mobility, manage pain,
              and address posture and spine-related concerns through a patient-focused approach.
              At {business.clinicName} in Varanasi, the focus is on understanding each patient's
              individual concern and developing a personalized care approach.
            </p>
            <p className="mt-4 text-ink leading-relaxed">
              The clinic offers chiropractic and rehabilitation-focused services designed to support
              better movement, physical function and overall well-being. The approach emphasizes
              patient education, careful assessment and appropriate non-surgical care options where suitable.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <BookButton testId="about-book-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Qualifications */}
      <section className="bg-soft py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-white">
                <GraduationCap className="h-6 w-6" />
              </div>
              <h2 className="font-heading font-extrabold text-navy text-2xl sm:text-3xl">Qualifications</h2>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <p className="text-ink leading-relaxed font-medium">{business.qualifications}</p>
              <p className="mt-4 text-sm text-muted-foreground">
                {business.role} · 10+ years of professional experience
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex items-center gap-3 mb-5">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-orange text-white">
                <Target className="h-6 w-6" />
              </div>
              <h2 className="font-heading font-extrabold text-navy text-2xl sm:text-3xl">Areas of Expertise</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {areasOfExpertise.map((a) => (
                <div key={a} className="flex items-center gap-2.5 bg-white rounded-xl px-4 py-3.5 shadow-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-orange shrink-0" />
                  <span className="text-sm font-semibold text-navy">{a}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Treatment Philosophy"
            title="How We Care For You"
            sub="A thoughtful, evidence-conscious approach centred on understanding, comfort and long-term function."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {philosophy.map((p, i) => (
              <Reveal key={p.title} delay={i * 70}>
                <div className="h-full bg-soft rounded-2xl p-7">
                  <HeartHandshake className="h-8 w-8 text-brand-orange" />
                  <h3 className="mt-4 font-heading font-bold text-navy text-lg">{p.title}</h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="bg-soft py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { img: images.doctor, cap: "Dr. Satyam Jaiswal" },
              { img: images.consult, cap: "Consultation & assessment" },
              { img: images.clinic, cap: "Modern clinic environment" },
            ].map((g, i) => (
              <Reveal key={i} delay={i * 80}>
                <figure className="overflow-hidden rounded-2xl shadow-sm bg-white">
                  <img src={g.img} alt={g.cap} loading="lazy" className="h-56 w-full object-cover" />
                  <figcaption className="px-5 py-4 text-sm font-semibold text-navy">{g.cap}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link to={routes.treatments} className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              View Treatments <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to={routes.testimonials} className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              Patient Testimonials <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA title="Ready to Meet Dr. Satyam?" />
    </>
  );
}
